<script setup lang="ts">
import TeacherIllustration from '~/assets/images/illustrations/teachers.svg';
import type { FetchError } from 'ofetch';
import { TRACKING_GROUP } from "~/composables/useTracking";
import { REGISTRATION_TRACKING } from "@/enums/tracking/registration.enums";
import { NEXT_AFTER_REGISTRATION } from '~/types/localstorage-keys.types';

interface signupErrorResponse {
  message: string;
  errors: {
    email?: string[] | null;
    password?: string[] | null;
    termsAndConditions?: string[] | null;
  };
}

const { dispatchMessage } = useSystemMessages();
const { dispatchTrackingEvent, hashEmailAndPush, hashEmailAndPushOnBlur, virtualPageView } = useTracking();

virtualPageView('authentication', 'signup');

const route = useRoute();

const { user } = useUserStore();
const { getUser } = useAuthentication();

const {
  providers,
  getSocialAuthenticationURL,
} = useSocialAuthentication();
const socialLoginStatus = route.query.status as string;
const socialLoginHint = route.query.hint as string | null;

const isSocialSignUp = computed(() => !!route.query.provider);

const signInLink = computed(() => {
  const link: any = {
    path: '/auth/login',
  };

  if (route.query.next) {
    link.query = { next: route.query.next };
  }

  return link;
});

const isSubmitting = ref(false);

const formData = reactive({
  email: '',
  password: '',
  termsAndConditions: false,
});
const submissionError = reactive({
  email: null,
  password: null,
  termsAndConditions: null,
} as signupErrorResponse['errors']);

const canSubmit = computed(() => {
  return !isSubmitting.value && formData.email && formData.password && formData.termsAndConditions;
});

const reasonsToSignUp = [
  'Access over 120 courses and workshops',
  '1300+ hours of medical content',
  '17 different medical specialties covered',
  'Download handbooks and other course resources',
];

const submitForm = async () => {
  isSubmitting.value = true;
  resetSubmissionErrors();

  let registrationSource = route.query.source as string;

  try {
    await hashEmailAndPush(formData.email);

    await useAuthenticatedFetch<{}>('/api/auth/register', {
      method: 'POST',
      credentials: 'include',
      body: {
        email: formData.email,
        password: formData.password,
        terms_and_conditions: formData.termsAndConditions,
        registration_source: registrationSource ? decodeURIComponent(registrationSource) : null,
      },
    });

    await finishSignUp();
  } catch (error) {
    const e = error as FetchError<signupErrorResponse>;

    if ('statusCode' in e && e.statusCode === 422) {

      const fieldKeys = e.data ? Object.keys(e.data.errors) as (keyof signupErrorResponse['errors'])[] : [];

      for (const key of fieldKeys) {
        if (e.data && e.data.errors[key]) {
          submissionError[key as keyof typeof submissionError] = e.data.errors[key];
        }
      }
    }
    else {
      dispatchMessage(
        'An error occurred',
        'error',
        (error as any).message || 'Unable to submit your request, please try again later.',
        20000,
      );
      console.error(error);
    }

    isSubmitting.value = false;
  }
};

const finishSignUp = async () => {
  // Fetch the user to update the user store
  await getUser();

  dispatchTrackingEvent(REGISTRATION_TRACKING.ACCOUNT_CREATED, { method: 'email' }, TRACKING_GROUP.REGISTRATION_TRACKING);

  navigateTo('/auth/verify-email');
};

const resetSubmissionErrors = () => {
  for (const key in submissionError) {
    submissionError[key as keyof typeof submissionError] = null;
  }
};

// todo: should even name and origin prop. value be enums?
const handleLogInClick = () => {
  dispatchTrackingEvent('login', {
    origin: 'signup_page',
  });
};

onMounted(() => {
  if (user.value) {
    navigateTo(route.query.next as string || '/dashboard');
    return;
  }

  if (route.query.next) {
    localStorage.setItem(NEXT_AFTER_REGISTRATION, route.query.next as string);
  }

  if (route.query.email) {
    formData.email = route.query.email as string;
  }

  if (route.query.provider && providers.some(p => p.name === route.query.provider)) {
    window.location.href = getSocialAuthenticationURL(route.query.provider as string, 'signup');
  }
});

definePageMeta({
  layout: 'auth',
});

useSeoMeta({
  title: 'Sign up | Medmastery',
});

useHead({
  title: 'Sign up',
});
</script>

<template>
  <div class="page page--sign-up">
    <SplitView class="content">
      <template #main>
        <form
          v-if="!isSocialSignUp"
          class="flex flex-col gap-4"
        >
          <header class="form-heading">
            <AuthPagesFormHeader
              title="Medmastery Sign Up"
              subtitle="Boost your clinical skills and gain confidence - Join Medmastery today!"
            />
          </header>

          <div class="flex flex-col gap-4">
            <MField
              label="Email *"
              :error="submissionError.email?.join(' ') || false"
            >
              <InputText
                v-model="formData.email"
                :invalid="submissionError.email !== null"
                type="email"
                autocomplete="username"
                :disabled="isSubmitting"
                @blur="hashEmailAndPushOnBlur"
              />
            </MField>
            <MField
              label="Password *"
              :error="submissionError.password?.join(' ') || false"
            >
              <Password
                v-model="formData.password"
                toggle-mask
                :input-props="{autocomplete: 'new-password'}"
                :invalid="submissionError.password !== null"
                :feedback="false"
                :disabled="isSubmitting"
              />
            </MField>
            <MField
              class="terms-and-conditions"
              inline-label
              :error="submissionError.termsAndConditions?.join(' ') || false"
            >
              <Checkbox
                v-model="formData.termsAndConditions"
                input-id="accept-terms"
                :invalid="submissionError.termsAndConditions !== null"
                binary
              />
              <label
                for="accept-terms"
                class="f-14-regular ml-2"
              >
                I accept the
                <a
                  target="_blank"
                  href="https://www.medmastery.com/terms"
                >Terms of Use</a>,
                <a
                  target="_blank"
                  href="https://www.medmastery.com/privacy-policy"
                >Privacy Policy</a>, and
                <a
                  target="_blank"
                  href="https://www.medmastery.com/newsletter-privacy-policy"
                >Newsletter Policy</a>.
              </label>
            </MField>
            <Button
              label="Sign up"
              :disabled="!canSubmit"
              :loading="isSubmitting"
              @click="submitForm"
            >
              <template #icon>
                <MIcon
                  :icon="isSubmitting ? 'loading' : 'arrow-right'"
                  class="p-button-icon p-button-icon-right"
                />
              </template>
            </Button>
          </div>

          <div class="alternate-actions f-14-regular">
            Already have an account?
            <NuxtLink
              :to="signInLink"
              @click="handleLogInClick()"
            >
              Log in here
            </NuxtLink>
          </div>

          <Divider
            :align="'center'"
            class="my-4 text-14 leading-normal font-bold"
          >
            OR
          </Divider>
          <div class="social-signup gap-4">
            <AuthPagesSocialLogin
              :button-prefix="'Sign up'"
              :social-login-hint="socialLoginHint"
              :social-login-status="socialLoginStatus"
            />
          </div>
          <div class="disclaimer text-14 leading-150">
            By proceeding with social sign up I accept the <a
              target="_blank"
              href="https://www.medmastery.com/terms"
            >Terms of
              Use</a>,
            <a
              target="_blank"
              href="https://www.medmastery.com/privacy-policy"
            >Privacy Policy</a>, and
            <a
              target="_blank"
              href="https://www.medmastery.com/newsletter-privacy-policy"
            >Newsletter Policy</a>.
          </div>
        </form>
        <div
          v-else
          class="processing-signup"
        >
          <MLoadingIndicator />
        </div>
      </template>
      <template #aside>
        <div class="flex flex-col gap-8">
          <TeacherIllustration class="w-full h-auto" />
          <AuthPagesBenefits
            :benefits="reasonsToSignUp"
            :icon-color="'yellow'"
          />
        </div>
      </template>
    </SplitView>
  </div>
</template>

<style lang="scss">
@import '@mui/assets/styles/utility.scss';
@import '@mui/assets/styles/font-styles.scss';

.page--sign-up {
  @include breakpoint ('medium') {
    background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 50%, var(--palette-neutral-10) 50%, var(--palette-neutral-10) 100%);
  }

  .content {
    @include container-sizer('page');

    height: 100dvh;
  }

  .processing-signup {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 350px;
  }

  .p-divider-content {
    background-color: var(--palette-neutral-5);

    @include breakpoint ('medium') {
      background-color: white;
    }
  }
}
</style>